import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { APIError, ClientError, useErrorHandler, useLocale, useMutation, useQuery, useClient } from 'shared-ui';

import {
  changeOwnPasswordWithNoConfirmation,
  changeUserPasswordWithNoConfirmation,
  ChangePasswordWithTokenResponse,
} from 'api/users/users';
import { AppMessages } from 'i18n/messages';
import { QueryKeyEnum } from 'core/global.enum';
import { fetchPasswordPolicy } from 'api/auth/auth';
import { IS_UPDATE_PASSWORD_REQUIRED_KEY } from '../UpdatePasswordModalContextController';

import { UpdatePasswordContainerProps, UpdatePasswordForm } from './UpdatePassword.types';
import { UpdatePassword } from './UpdatePassword';
import { useUpdatePasswordValidation } from './UpdatePassword.validation';

export const UpdatePasswordContainer = ({
  isModalOpen,
  setIsModalOpen,
  user,
  type,
  modalBackdropNotTransparent,
}: UpdatePasswordContainerProps) => {
  const { formatMessage } = useLocale();
  const { handleError } = useErrorHandler();
  const { axios, authStorage } = useClient();
  const { data: passwordPolicy } = useQuery([QueryKeyEnum.PASSWORD_POLICY], fetchPasswordPolicy, {
    enabled: isModalOpen && !!user,
  });
  const isRequiredType = type === 'required';

  useEffect(() => {
    if (isRequiredType && isModalOpen && !!user) {
      localStorage.setItem(IS_UPDATE_PASSWORD_REQUIRED_KEY, 'true');
    }
  }, [isModalOpen, user]);

  const { mutate: onChangeUserPassword, isLoading } = useMutation(
    isRequiredType ? changeOwnPasswordWithNoConfirmation : changeUserPasswordWithNoConfirmation,
    {
      onSuccess: (data: ChangePasswordWithTokenResponse | void) => {
        toast.dark(formatMessage({ id: AppMessages['profile.changePassword.success'] }));

        // If this is a required password change (own password), update the stored token
        if (isRequiredType && data && 'accessToken' in data) {
          const tokenData = data as ChangePasswordWithTokenResponse;

          // Update stored tokens
          authStorage.accessToken = tokenData.accessToken.token;
          // Calculate absolute expiry timestamp: current time + relative expiry time
          const expiresInMs =
            typeof tokenData.accessToken.expiresIn === 'string'
              ? parseInt(tokenData.accessToken.expiresIn, 10)
              : tokenData.accessToken.expiresIn;
          authStorage.expires = Date.now() + expiresInMs;

          // Update axios authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData.accessToken.token}`;
        }

        localStorage.removeItem(IS_UPDATE_PASSWORD_REQUIRED_KEY);
        setIsModalOpen(false);
      },
      onError: (error: ClientError<APIError>) => {
        const errorFields = error.response?.data.fields;

        const isSamePasswordError = errorFields?.find(
          ({ type, field }) => type === 'same_password' && field === 'newPassword',
        );
        if (isSamePasswordError) {
          return formMethods.setError('newPassword', {
            message: formatMessage({ id: AppMessages['profile.changePassword.validation.newDifference'] }),
          });
        }

        handleError({ error, omitAPIErrorMessage: true });
      },
    },
  );

  const { schema } = useUpdatePasswordValidation(user, passwordPolicy);
  const formMethods = useForm<UpdatePasswordForm>({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const onSubmit = (body: UpdatePasswordForm) => {
    onChangeUserPassword({ body, id: user?.id });
  };

  const onClose = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    formMethods.reset();
  }, [isModalOpen]);

  return (
    <FormProvider {...formMethods}>
      <UpdatePassword
        isModalOpen={isModalOpen}
        isLoading={isLoading}
        onSubmit={onSubmit}
        onClose={isRequiredType ? undefined : onClose}
        passwordPolicy={passwordPolicy}
        modalBackdropNotTransparent={modalBackdropNotTransparent}
      />
    </FormProvider>
  );
};
